/** Inline SVG mockup illustrations for onboarding tutorial slides */
import { motion } from "framer-motion";

const BG = "#161b26";
const CARD = "#1e2433";
const BLUE = "#4d8ef7";
const TEXT = "#8b95a8";
const TEXT_DIM = "#5a6478";
const GREEN = "#34d399";
const PURPLE = "#a78bfa";
const ORANGE = "#fb923c";

const fade = (delay: number) => ({
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay, ease: "easeOut" as const },
});

const pop = (delay: number) => ({
  initial: { opacity: 0, scale: 0.7 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.3, delay, ease: "easeOut" as const },
});

const MG = motion.g;
const MR = motion.rect;
const MC = motion.circle;
const ML = motion.line;
const MP = motion.polyline;

/* 1 — Welcome / Dashboard overview */
export function IllustrationWelcome() {
  return (
    <svg width="260" height="150" viewBox="0 0 260 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="260" height="150" rx="10" fill={BG} />
      {/* Sidebar */}
      <MG {...fade(0.05)}>
        <rect x="8" y="8" width="44" height="134" rx="6" fill={CARD} />
        <circle cx="30" cy="24" r="6" fill={BLUE} opacity={0.6} />
        <rect x="18" y="40" width="24" height="3" rx="1.5" fill={TEXT_DIM} />
        <rect x="18" y="50" width="24" height="3" rx="1.5" fill={TEXT_DIM} />
        <rect x="18" y="60" width="24" height="3" rx="1.5" fill={BLUE} opacity={0.5} />
        <rect x="18" y="70" width="24" height="3" rx="1.5" fill={TEXT_DIM} />
        <rect x="18" y="80" width="24" height="3" rx="1.5" fill={TEXT_DIM} />
      </MG>
      {/* KPI cards */}
      <MG {...pop(0.15)}>
        <rect x="62" y="8" width="58" height="36" rx="6" fill={CARD} />
        <rect x="70" y="16" width="20" height="4" rx="2" fill={TEXT_DIM} />
        <text x="70" y="36" fontSize="10" fontWeight="bold" fill={BLUE}>128</text>
      </MG>
      <MG {...pop(0.25)}>
        <rect x="126" y="8" width="58" height="36" rx="6" fill={CARD} />
        <rect x="134" y="16" width="20" height="4" rx="2" fill={TEXT_DIM} />
        <text x="134" y="36" fontSize="10" fontWeight="bold" fill={GREEN}>87%</text>
      </MG>
      <MG {...pop(0.35)}>
        <rect x="190" y="8" width="62" height="36" rx="6" fill={CARD} />
        <rect x="198" y="16" width="20" height="4" rx="2" fill={TEXT_DIM} />
        <text x="198" y="36" fontSize="10" fontWeight="bold" fill={PURPLE}>42</text>
      </MG>
      {/* Chart area */}
      <MR {...fade(0.3)} x="62" y="52" width="190" height="90" rx="6" fill={CARD} />
      <ML {...fade(0.4)} x1="74" y1="60" x2="74" y2="130" stroke={TEXT_DIM} strokeWidth="0.5" />
      <ML {...fade(0.4)} x1="74" y1="130" x2="244" y2="130" stroke={TEXT_DIM} strokeWidth="0.5" />
      <MP
        {...{ initial: { pathLength: 0, opacity: 0 }, animate: { pathLength: 1, opacity: 1 }, transition: { duration: 0.8, delay: 0.5, ease: "easeOut" } }}
        points="74,120 100,105 120,112 140,90 160,95 180,78 200,82 220,70 240,75"
        stroke={BLUE} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/* 2 — Landing editor */
export function IllustrationLandings() {
  return (
    <svg width="260" height="150" viewBox="0 0 260 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="260" height="150" rx="10" fill={BG} />
      {/* Left panel */}
      <MG {...fade(0.05)}>
        <rect x="8" y="8" width="110" height="134" rx="6" fill={CARD} />
        <rect x="16" y="16" width="40" height="4" rx="2" fill={TEXT} />
        <rect x="16" y="28" width="90" height="14" rx="4" fill={BG} stroke={TEXT_DIM} strokeWidth="0.5" />
        <rect x="20" y="33" width="40" height="4" rx="2" fill={TEXT_DIM} />
      </MG>
      <MG {...fade(0.15)}>
        <rect x="16" y="50" width="40" height="4" rx="2" fill={TEXT} />
        <rect x="16" y="62" width="90" height="14" rx="4" fill={BG} stroke={TEXT_DIM} strokeWidth="0.5" />
        <rect x="20" y="67" width="55" height="4" rx="2" fill={TEXT_DIM} />
      </MG>
      <MG {...fade(0.25)}>
        <rect x="16" y="84" width="40" height="4" rx="2" fill={TEXT} />
        <rect x="16" y="96" width="90" height="28" rx="4" fill={BG} stroke={TEXT_DIM} strokeWidth="0.5" />
        <rect x="20" y="101" width="70" height="3" rx="1.5" fill={TEXT_DIM} />
        <rect x="20" y="108" width="50" height="3" rx="1.5" fill={TEXT_DIM} />
      </MG>
      {/* Right panel – preview */}
      <MG {...fade(0.2)}>
        <rect x="126" y="8" width="126" height="134" rx="6" fill={CARD} />
        <rect x="146" y="24" width="86" height="100" rx="8" fill={BG} stroke={BLUE} strokeWidth="0.5" opacity={0.8} />
      </MG>
      <MC {...pop(0.35)} cx="189" cy="44" r="10" fill={BLUE} opacity={0.2} />
      <MC {...pop(0.4)} cx="189" cy="44" r="5" fill={BLUE} opacity={0.5} />
      <MG {...fade(0.45)}>
        <rect x="164" y="62" width="50" height="4" rx="2" fill={TEXT} />
        <rect x="170" y="72" width="38" height="3" rx="1.5" fill={TEXT_DIM} />
        <rect x="162" y="84" width="54" height="10" rx="4" fill={BG} stroke={TEXT_DIM} strokeWidth="0.5" />
      </MG>
      <MR {...pop(0.55)} x="162" y="100" width="54" height="14" rx="6" fill={BLUE} />
      <MR {...fade(0.6)} x="174" y="105" width="30" height="4" rx="2" fill="#fff" opacity={0.9} />
    </svg>
  );
}

/* 3 — Templates grid */
export function IllustrationTemplates() {
  const colors = [BLUE, GREEN, PURPLE, ORANGE, BLUE, GREEN];
  return (
    <svg width="260" height="150" viewBox="0 0 260 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="260" height="150" rx="10" fill={BG} />
      <MR {...fade(0.05)} x="16" y="12" width="60" height="5" rx="2.5" fill={TEXT} />
      <MG {...pop(0.1)}>
        <rect x="200" y="10" width="44" height="12" rx="4" fill={BLUE} />
        <rect x="208" y="14" width="28" height="4" rx="2" fill="#fff" opacity={0.9} />
      </MG>
      {[0, 1, 2].map((col) =>
        [0, 1].map((row) => {
          const x = 16 + col * 78;
          const y = 30 + row * 56;
          const c = colors[row * 3 + col];
          const delay = 0.12 + (row * 3 + col) * 0.08;
          return (
            <MG key={`${col}-${row}`} {...pop(delay)}>
              <rect x={x} y={y} width="70" height="50" rx="6" fill={CARD} />
              <rect x={x + 6} y={y + 6} width="58" height="22" rx="4" fill={c} opacity={0.15} />
              <circle cx={x + 35} cy={y + 17} r="5" fill={c} opacity={0.5} />
              <rect x={x + 10} y={y + 34} width="36" height="3" rx="1.5" fill={TEXT_DIM} />
              <rect x={x + 10} y={y + 41} width="24" height="3" rx="1.5" fill={TEXT_DIM} opacity={0.5} />
            </MG>
          );
        })
      )}
    </svg>
  );
}

/* 4 — Leads table */
export function IllustrationLeads() {
  return (
    <svg width="260" height="150" viewBox="0 0 260 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="260" height="150" rx="10" fill={BG} />
      <MG {...fade(0.05)}>
        <rect x="12" y="10" width="140" height="14" rx="4" fill={CARD} stroke={TEXT_DIM} strokeWidth="0.5" />
        <circle cx="22" cy="17" r="4" stroke={TEXT_DIM} strokeWidth="0.8" fill="none" />
        <rect x="30" y="15" width="50" height="3" rx="1.5" fill={TEXT_DIM} />
      </MG>
      <MG {...pop(0.1)}>
        <rect x="200" y="10" width="48" height="14" rx="4" fill={BLUE} opacity={0.8} />
        <rect x="210" y="15" width="28" height="4" rx="2" fill="#fff" opacity={0.9} />
      </MG>
      {/* Header */}
      <MG {...fade(0.15)}>
        <rect x="12" y="32" width="236" height="14" rx="0" fill={CARD} />
        <rect x="18" y="37" width="30" height="3" rx="1.5" fill={TEXT} />
        <rect x="70" y="37" width="40" height="3" rx="1.5" fill={TEXT} />
        <rect x="140" y="37" width="30" height="3" rx="1.5" fill={TEXT} />
        <rect x="200" y="37" width="28" height="3" rx="1.5" fill={TEXT} />
      </MG>
      {/* Rows */}
      {Array.from({ length: 5 }).map((_, i) => {
        const y = 50 + i * 18;
        const isEven = i % 2 === 0;
        return (
          <MG key={i} {...fade(0.2 + i * 0.07)}>
            {isEven && <rect x="12" y={y} width="236" height="16" fill={CARD} opacity={0.3} />}
            <rect x="18" y={y + 5} width="38" height="3" rx="1.5" fill={TEXT_DIM} />
            <rect x="70" y={y + 5} width="50" height="3" rx="1.5" fill={TEXT_DIM} />
            <rect x="140" y={y + 5} width="34" height="3" rx="1.5" fill={TEXT_DIM} />
            <rect x="200" y={y + 2} width="24" height="8" rx="4" fill={i < 2 ? GREEN : BLUE} opacity={0.2} />
            <rect x="204" y={y + 5} width="16" height="3" rx="1.5" fill={i < 2 ? GREEN : BLUE} opacity={0.7} />
          </MG>
        );
      })}
    </svg>
  );
}

/* 5 — Link in Bio mobile preview */
export function IllustrationLinkInBio() {
  const pillColors = [BLUE, GREEN, PURPLE, ORANGE];
  return (
    <svg width="260" height="150" viewBox="0 0 260 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      <rect width="260" height="150" rx="10" fill={BG} />
      {/* Phone frame */}
      <MG {...fade(0.05)}>
        <rect x="85" y="6" width="90" height="138" rx="12" fill={CARD} stroke={TEXT_DIM} strokeWidth="0.8" />
        <rect x="112" y="8" width="36" height="5" rx="2.5" fill={BG} />
      </MG>
      {/* Avatar */}
      <MC {...pop(0.15)} cx="130" cy="32" r="12" fill={BLUE} opacity={0.2} />
      <MC {...pop(0.2)} cx="130" cy="32" r="7" fill={BLUE} opacity={0.4} />
      {/* Name & bio */}
      <MG {...fade(0.25)}>
        <rect x="110" y="50" width="40" height="4" rx="2" fill={TEXT} />
        <rect x="104" y="58" width="52" height="3" rx="1.5" fill={TEXT_DIM} />
      </MG>
      {/* Link pills */}
      {pillColors.map((c, i) => {
        const y = 70 + i * 18;
        return (
          <MG key={i} {...pop(0.3 + i * 0.08)}>
            <rect x="95" y={y} width="70" height="14" rx="7" fill={c} opacity={0.12} stroke={c} strokeWidth="0.5" strokeOpacity={0.3} />
            <rect x="108" y={y + 5} width="44" height="3" rx="1.5" fill={c} opacity={0.7} />
          </MG>
        );
      })}
      {/* Side decorations */}
      <MG {...fade(0.5)}>
        <rect x="12" y="65" width="56" height="44" rx="6" fill={CARD} opacity={0.5} />
        <rect x="20" y="73" width="30" height="4" rx="2" fill={TEXT_DIM} />
        <circle cx="24" cy="90" r="5" fill={BLUE} opacity={0.3} />
        <circle cx="38" cy="90" r="5" fill={PURPLE} opacity={0.3} />
        <circle cx="52" cy="90" r="5" fill={GREEN} opacity={0.3} />
      </MG>
      <MG {...fade(0.55)}>
        <rect x="192" y="65" width="56" height="44" rx="6" fill={CARD} opacity={0.5} />
        <rect x="200" y="73" width="30" height="4" rx="2" fill={TEXT_DIM} />
        <text x="200" y="96" fontSize="11" fontWeight="bold" fill={BLUE} opacity={0.6}>247</text>
      </MG>
    </svg>
  );
}

const illustrations = [
  IllustrationWelcome,
  IllustrationLandings,
  IllustrationTemplates,
  IllustrationLeads,
  IllustrationLinkInBio,
];

export function OnboardingIllustration({ step }: { step: number }) {
  const Comp = illustrations[step];
  return Comp ? <Comp /> : null;
}
